// src/components/Dashboard/CoachDashboard/WeekCalendarWidget.jsx
// Horizontal week view of upcoming events

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { 
  FaCalendarWeek, FaArrowRight, FaArrowLeft 
} from 'react-icons/fa';
import styles from './WeekCalendarWidget.module.css';

const WeekCalendarWidget = ({ programs, selectedProgramId, refreshTrigger }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);

  // Get selected program or all
  const selectedProgram = programs?.find(p => p._id === selectedProgramId);

  // Fetch events for the week
  useEffect(() => {
    const fetchEvents = async () => {
      if (!programs || programs.length === 0) {
        setEvents([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const auth = getAuth();
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        
        const headers = token ? {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        } : {};
        
        // Fetch events from selected program or all programs
        const programsToFetch = selectedProgram ? [selectedProgram] : programs;
        
        const allEvents = await Promise.all(
          programsToFetch.map(async (program) => {
            try {
              const response = await fetch(`${backendUrl}/api/events/${program._id}`, { headers });
              if (!response.ok) return [];
              const data = await response.json();
              return (data || []).map(e => ({
                ...e,
                programId: program._id,
                programName: program.name,
                clubId: program.clubId,
                sport: program.sport
              }));
            } catch {
              return [];
            }
          })
        );

        setEvents(allEvents.flat());
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [programs, selectedProgram, refreshTrigger]);

  // Get week days
  const getWeekDays = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) + (weekOffset * 7));
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      
      const dayEvents = events.filter(e => {
        const eventDate = new Date(e.dateTime?.start);
        return eventDate.toDateString() === date.toDateString();
      }).sort((a, b) => new Date(a.dateTime?.start) - new Date(b.dateTime?.start));

      days.push({
        date,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: date.getDate(),
        isToday: date.toDateString() === today.toDateString(),
        events: dayEvents
      });
    }
    
    return days;
  };

  const getEventTypeIcon = (type, sport) => {
    const sportIcons = {
      'Soccer': '⚽',
      'Basketball': '🏀',
      'Football': '🏈',
      'Volleyball': '🏐',
      'default': '📅'
    };
    
    if (type?.toLowerCase() === 'game' || type?.toLowerCase() === 'match') {
      return sportIcons[sport] || sportIcons.default;
    }
    return '🏃';
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const handleEventClick = (event) => {
    navigate(`/program/${event.programId}/events?eventId=${event._id}`);
  };

  const handlePrevWeek = () => setWeekOffset(prev => prev - 1);
  const handleNextWeek = () => setWeekOffset(prev => prev + 1);
  const handleThisWeek = () => setWeekOffset(0);

  const weekDays = getWeekDays();
  const weekLabel = weekOffset === 0 
    ? 'This Week' 
    : weekOffset === 1 
      ? 'Next Week' 
      : weekOffset === -1 
        ? 'Last Week' 
        : `${weekDays[0].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDays[6].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaCalendarWeek className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Upcoming Week</h3>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FaCalendarWeek className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>Upcoming Week</h3>
        <div className={styles.weekNav}>
          <button className={styles.navBtn} onClick={handlePrevWeek}>
            <FaArrowLeft />
          </button>
          <span className={styles.weekLabel}>{weekLabel}</span>
          <button className={styles.navBtn} onClick={handleNextWeek}>
            <FaArrowRight />
          </button>
          {weekOffset !== 0 && (
            <button className={styles.todayBtn} onClick={handleThisWeek}>
              Today
            </button>
          )}
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.weekGrid}>
          {weekDays.map((day, idx) => (
            <div 
              key={idx} 
              className={`${styles.dayColumn} ${day.isToday ? styles.today : ''}`}
            >
              <div className={styles.dayHeader}>
                <span className={styles.dayName}>{day.dayName}</span>
                <span className={styles.dayNum}>{day.dayNum}</span>
              </div>
              
              <div className={styles.dayEvents}>
                {day.events.length === 0 ? (
                  <div className={styles.noEvents}>-</div>
                ) : (
                  day.events.slice(0, 2).map(event => (
                    <div 
                      key={event._id}
                      className={styles.eventChip}
                      onClick={() => handleEventClick(event)}
                    >
                      <span className={styles.eventIcon}>
                        {getEventTypeIcon(event.eventType, event.sport)}
                      </span>
                      <div className={styles.eventDetails}>
                        <span className={styles.eventTitle}>{event.title}</span>
                        <span className={styles.eventTime}>{formatTime(event.dateTime?.start)}</span>
                      </div>
                    </div>
                  ))
                )}
                {day.events.length > 2 && (
                  <span className={styles.moreEvents}>+{day.events.length - 2} more</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeekCalendarWidget;

